import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useOrders from '../../hooks/useOrders';
import { formatCurrency, formatDate } from '../../utils/formatters';
import './Dashboard.css';

const ProfileIncompleteBanner = () => (
  <div className="dashboard__profile-banner">
    <div className="dashboard__profile-banner-inner">
      <svg className="dashboard__profile-banner-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
      <div className="dashboard__profile-banner-body">
        <p className="dashboard__profile-banner-title">Complete your company profile</p>
        <p className="dashboard__profile-banner-text">
          Add your business type, billing contact, and addresses to place orders.
        </p>
      </div>
      <Link to="/profile" className="dashboard__profile-banner-cta">
        Complete Profile →
      </Link>
    </div>
  </div>
);

const STATUS_COLORS = {
  pending:          '#bc9038',
  confirmed:        '#4169E1',
  in_progress:      '#9370DB',
  fulfilled:        '#32CD32',
  out_for_delivery: '#20B2AA',
  delivered:        '#228B22',
  cancelled:        '#DC143C',
};

const STATUS_LABELS = {
  pending:          'Pending',
  confirmed:        'Confirmed',
  in_progress:      'In Progress',
  fulfilled:        'Fulfilled',
  out_for_delivery: 'Out for Delivery',
  delivered:        'Delivered',
  cancelled:        'Cancelled',
};

const Dashboard = () => {
  const { user, profile } = useAuth();
  const { orders, loading } = useOrders({ autoFetch: true });

  const greeting = profile?.company?.company_name ?? profile?.company_name ?? profile?.first_name ?? user?.email;
  const recentOrders = orders.slice(0, 5);
  const profileIncomplete =
    profile?.company?.company_profile_complete === false ||
    profile?.company_profile_complete === false;

  return (
    <div className="dashboard">

      {/* ── Welcome banner ─────────────────────────────────── */}
      <div className="dashboard__banner">
        <div className="dashboard__banner-inner">
          <p className="dashboard__eyebrow">Deep Roots Milling — Wholesale Portal</p>
          <h1 className="dashboard__greeting">Welcome back,<br />{greeting}</h1>
          <div className="dashboard__banner-divider">
            <span className="dashboard__banner-line" />
            <span className="dashboard__banner-ornament">❧</span>
            <span className="dashboard__banner-line" />
          </div>
          {!loading && orders.length > 0 && (
            <p className="dashboard__banner-stat">
              {orders.length} {orders.length === 1 ? 'order' : 'orders'} placed
            </p>
          )}
        </div>
      </div>

      {profileIncomplete && <ProfileIncompleteBanner />}

      <div className="dashboard__content">

        {/* ── Quick actions ───────────────────────────────────── */}
        <section className="dashboard__section">
          <p className="dashboard__section-eyebrow">Quick Actions</p>
          <div className="dashboard__actions">

            {profileIncomplete ? (
              <div className="dashboard__action-card dashboard__action-card--disabled" title="Complete your company profile to place orders">
                <div className="dashboard__action-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                  </svg>
                </div>
                <div className="dashboard__action-body">
                  <h3 className="dashboard__action-title">Place an Order</h3>
                  <p className="dashboard__action-desc">Complete your company profile to start ordering.</p>
                </div>
                <span className="dashboard__action-arrow dashboard__action-lock">🔒</span>
              </div>
            ) : (
              <Link to="/products" className="dashboard__action-card">
                <div className="dashboard__action-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                  </svg>
                </div>
                <div className="dashboard__action-body">
                  <h3 className="dashboard__action-title">Place an Order</h3>
                  <p className="dashboard__action-desc">Browse our catalog of stone-milled flours and grains.</p>
                </div>
                <span className="dashboard__action-arrow">→</span>
              </Link>
            )}

            <Link to="/orders" className="dashboard__action-card">
              <div className="dashboard__action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
                </svg>
              </div>
              <div className="dashboard__action-body">
                <h3 className="dashboard__action-title">Order History</h3>
                <p className="dashboard__action-desc">Review past orders, check delivery status, and reorder.</p>
              </div>
              <span className="dashboard__action-arrow">→</span>
            </Link>

            <Link to="/profile" className="dashboard__action-card">
              <div className="dashboard__action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              </div>
              <div className="dashboard__action-body">
                <h3 className="dashboard__action-title">My Account</h3>
                <p className="dashboard__action-desc">Update contact details, address, and account preferences.</p>
              </div>
              <span className="dashboard__action-arrow">→</span>
            </Link>

          </div>
        </section>

        {/* ── Recent orders ───────────────────────────────────── */}
        <section className="dashboard__section">
          <div className="dashboard__section-header">
            <p className="dashboard__section-eyebrow">Recent Orders</p>
            <Link to="/orders" className="dashboard__view-all">View all →</Link>
          </div>

          {loading ? (
            <div className="dashboard__orders-shell dashboard__orders-shell--loading">
              <p>Loading orders…</p>
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="dashboard__orders-shell dashboard__orders-shell--empty">
              <p className="dashboard__orders-empty-title">No orders yet</p>
              {profileIncomplete ? (
                <Link to="/profile" className="dashboard__cta-link">
                  Complete your profile to place your first order →
                </Link>
              ) : (
                <Link to="/products" className="dashboard__cta-link">
                  Browse our catalog to place your first order →
                </Link>
              )}
            </div>
          ) : (
            <div className="dashboard__orders-shell">
              {recentOrders.map((order, index) => {
                const statusColor = STATUS_COLORS[order.status] || '#9c8f7e';
                const statusLabel = STATUS_LABELS[order.status] || order.status;
                const total = order.total_amount_dollars ?? order.total_amount;

                return (
                  <Link
                    key={order.id}
                    to={`/orders/${order.id}`}
                    className="dashboard__order-row"
                    style={{ animationDelay: `${0.3 + index * 0.05}s` }}
                  >
                    <div className="dashboard__order-row-left">
                      <span className="dashboard__order-number">{order.order_number}</span>
                      <span className="dashboard__order-date">
                        {order.created_at ? formatDate(order.created_at) : '—'}
                      </span>
                    </div>
                    <div className="dashboard__order-row-right">
                      <span className="dashboard__order-total">
                        {total != null ? formatCurrency(total) : '—'}
                      </span>
                      <span
                        className="dashboard__order-status"
                        style={{
                          color: statusColor,
                          backgroundColor: `${statusColor}12`,
                          borderColor: `${statusColor}35`,
                        }}
                      >
                        {statusLabel}
                      </span>
                      <span className="dashboard__order-arrow">→</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

export default Dashboard;
